import { toNumber } from '@/helpers/parser.ts'
import { useMappingStore } from '@/stores/mappingStore.ts'
import type { FormFieldMappingKey } from '@/ts/types/form-field-mapping-key.ts'
import { useDataAttributes } from '@/composables/dataAttributes.ts'
import { PREFILL_KEYS, type PrefillKey } from '@/config/prefill.config.ts'
import { CalculationService } from '@/services/calculation.service.ts'
import { FieldRuleService } from '@/services/field-rule.service.ts'
import { useTracking } from '@/composables/tracking'
import { useTrackingInteractionStore } from '@/stores/trackingInteractionStore'
import { defaultCalculationConfig } from '@/config/default.config'

const LEASING_CALCULATOR_REDIRECT_URL = import.meta.env.VITE_LEASING_CALCULATOR_REDIRECT_URL || ''

type ValidationResult = { isValid: boolean; error: string }
const validationResult = (isValid: boolean, error = ''): ValidationResult => ({ isValid, error })

/**
 * Service for handling prefill parameters for the Leasing Calculator, including extraction from URL, validation, application to form state, and building redirect URLs with prefill data.
 */
export const PrefillService = {
  /**
   * Collects and validates prefill parameters using the provided value retrieval function.
   * @param getRawValue - A function that takes a prefill key and returns its raw value as a string, number, null, or undefined.
   * @returns An object containing valid prefill parameters and their numeric values.
   */
  collectPrefillParams(getRawValue: (key: PrefillKey) => string | number | null | undefined): Partial<Record<PrefillKey, number>> {
    const prefillParams: Partial<Record<PrefillKey, number>> = {}

    PREFILL_KEYS.forEach((key) => {
      const rawValue = getRawValue(key)
      if (rawValue === null || typeof rawValue === 'undefined' || rawValue === '') return

      const parsedValue = PrefillService.parsePrefillValue(key, String(rawValue))
      if (parsedValue === null) return

      prefillParams[key] = parsedValue
    })

    return prefillParams
  },

  /**
   * Extracts valid prefill values from the given search string.
   * If no search string is provided, the current URL query is used.
   * @param search - The query string to extract prefill parameters from.
   * @returns An object containing valid prefill parameters and their numeric values.
   */
  getPrefillParams(search: string = window.location.search): Partial<Record<PrefillKey, number>> {
    const urlParams = new URLSearchParams(search)
    return PrefillService.collectPrefillParams((key) => {
      const rawValue = urlParams.get(key)
      return rawValue === null || rawValue.trim() === '' ? null : rawValue
    })
  },

  /**
   * Extracts valid prefill values from mount element data-attributes.
   * @returns An object containing valid prefill parameters and their numeric values.
   */
  getPrefillParamsFromDataAttributes(): Partial<Record<PrefillKey, number>> {
    const { prefillParams } = useDataAttributes()
    return PrefillService.collectPrefillParams((key) => prefillParams[key])
  },

  /**
   * Validates a field value against its validation rules and returns a validation result.
   * @param fieldValue - The value to validate
   * @param fieldRules - Array of validation rule functions
   * @returns Validation state with optional user-facing error message.
   */
  async validateFieldValue(fieldValue: any, fieldRules: Function[]): Promise<ValidationResult> {
    if (fieldRules.length === 0) return validationResult(true)

    const preparedRules = fieldRules.map((rule: Function) => rule(fieldValue))
    const results = await Promise.allSettled(preparedRules)

    for (const result of results) {
      if (result.status !== 'fulfilled') return validationResult(false)
      if (result.value !== true) {
        return validationResult(false, typeof result.value === 'string' ? result.value : '')
      }
    }
    return validationResult(true)
  },

  /**
   * Applies prefill parameters to the default Leasing Calculator form.
   * Field values are set directly (bypassing value rules) to avoid triggering multiple
   * partial calculations. Field validation rules are applied to set the valid flag.
   * Calculation is only triggered if all prefill fields are valid; validation errors
   * are displayed to the user instead.
   * @returns A promise that resolves when the prefill values have been applied.
   */
  async applyPrefillParams(): Promise<void> {
    const mappingStore = useMappingStore()
    const prefillParams = PrefillService.getPrefillParamsFromDataAttributes()

    if (!Object.keys(prefillParams).length) return

    let hasAppliedField = false
    let hasInvalidField = false

    for (const [key, value] of Object.entries(prefillParams)) {
      const field = mappingStore.getFieldsOfKey('calculator', key as FormFieldMappingKey).value[0]
      if (!field || typeof value !== 'number') continue
      hasAppliedField = true
      field.config.value = value

      const rules = FieldRuleService.getRules(field.config.field_rules)
      const { isValid, error } = await PrefillService.validateFieldValue(value, rules)
      field.config.valid = isValid
      field.config.error = error
      if (!isValid) hasInvalidField = true
    }

    // Only calculate if all prefill fields are valid and at least one field was set
    if (hasAppliedField && !hasInvalidField) {
      await CalculationService.updateAllCalculations()
    }
  },

  /**
   * Reads current prefill field values from the form state.
   * @returns Valid prefill values from mapped calculator fields.
   */
  getFormValues(): Partial<Record<PrefillKey, number>> {
    const mappingStore = useMappingStore()

    return PrefillService.collectPrefillParams((key) => {
      const field = mappingStore.getFieldsOfKey('calculator', key).value[0]
      // using default value if field is not valid to avoid pre-filling with invalid values.
      const fieldDefault = defaultCalculationConfig.fields.find((field) => field.name === key)?.default
      return field?.config?.valid ? field?.config?.value : fieldDefault
    })
  },

  /**
   * Builds a target URL with prefill query params.
   * @param prefillParams - Optional prefill params; if omitted they are read from current form state.
   */
  buildPrefillUrl(prefillParams?: Partial<Record<PrefillKey, number>>): string {
    const params = prefillParams ?? PrefillService.getFormValues()
    const queryParams = new URLSearchParams()

    Object.entries(params).forEach(([key, value]) => {
      if (typeof value === 'number') {
        queryParams.set(key, String(value))
      }
    })

    const queryString = queryParams.toString()
    return queryString ? `${LEASING_CALCULATOR_REDIRECT_URL}?${queryString}` : LEASING_CALCULATOR_REDIRECT_URL
  },

  /**
   * Redirects to production calculator and forwards valid prefill params as GET query.
   */
  redirectToDefaultCalculator(): void {
    const { variant } = useDataAttributes()
    const { resetMiniInteractionCount } = useTrackingInteractionStore()

    if (variant === 'mini') {
      useTracking().redirectMini()
      resetMiniInteractionCount()
    }
    window.open(PrefillService.buildPrefillUrl(), '_blank')
  },

  /**
   * Parses and validates a raw prefill value for a given key.
   * @param key - The prefill key to validate.
   * @param value - The raw value to parse and validate.
   * @returns The parsed numeric value if valid, otherwise null.
   */
  parsePrefillValue(key: PrefillKey, value: string): number | null {
    const parsedValue = toNumber(value)

    if (!Number.isFinite(parsedValue)) return null
    if (key === 'tax_class' && !PrefillService.isValidTaxClass(parsedValue)) return null

    return parsedValue
  },

  /**
   * Returns whether the value is a valid tax class (1-6).
   * @param value - The value to validate.
   * @returns True if the value is a valid tax class, otherwise false.
   */
  isValidTaxClass(value: number): boolean {
    return Number.isInteger(value) && value >= 1 && value <= 6
  }
}
